import { useAtomValue } from "jotai";
import type { ReactNode } from "react";
import { matchPath, Navigate, useLocation } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import {
	VENDOR_ALLOWED_ROUTE_PREFIXES,
	VENDOR_BLOCKED_ROUTE_PREFIXES,
	VENDOR_LANDING_ROUTE,
} from "~/shared/constants/page-roles.ts";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import { useSharedListsAccess } from "~/shared/hooks/use-shared-lists-access.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";

type VendorAccessGuardProps = {
	readonly children: ReactNode;
};

const matchesPrefix = (pathname: string, prefix: string): boolean =>
	pathname === prefix || pathname.startsWith(`${prefix}/`);

const isPathBlockedForVendor = (pathname: string): boolean =>
	VENDOR_BLOCKED_ROUTE_PREFIXES.some((prefix) =>
		matchesPrefix(pathname, prefix),
	);

const isPathAllowedForVendor = (pathname: string): boolean =>
	!isPathBlockedForVendor(pathname) &&
	VENDOR_ALLOWED_ROUTE_PREFIXES.some((prefix) =>
		matchesPrefix(pathname, prefix),
	);

export function VendorAccessGuard({ children }: VendorAccessGuardProps) {
	const { isVendor, isLoading } = useIsVendor();
	const { canManageSharedLists, isLoading: isAccessLoading } =
		useSharedListsAccess();
	const selectedCustomer = useAtomValue(selectedCustomerAtom);
	const { pathname } = useLocation();

	if (isLoading || isAccessLoading) {
		return <LoadingSpinner />;
	}

	const isSharedListAllowed =
		canManageSharedLists &&
		[
			routes.addressBook,
			routes.addressBookWithCustomer,
			routes.addressBookEdit,
			routes.commodities,
			routes.commoditiesWithCustomer,
		].some((path) => {
			const match = matchPath(path, pathname);
			return (
				match !== null &&
				(match.params["customerId"] === undefined ||
					match.params["customerId"] === String(selectedCustomer?.id))
			);
		});

	if (isVendor && !isPathAllowedForVendor(pathname) && !isSharedListAllowed) {
		return <Navigate replace to={VENDOR_LANDING_ROUTE} />;
	}

	return children;
}
